import { useEffect, useState, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bot, User as UserIcon, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ChatRole = "user" | "assistant";

interface Props {
  role: ChatRole;
  content: string;
  /** When true, animate the content with a typewriter effect (for the latest streaming assistant message). */
  streaming?: boolean;
  children?: ReactNode; // tool cards, CTAs, etc.
}

export function ChatBubble({ role, content, streaming, children }: Props) {
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(streaming ? "" : content);

  useEffect(() => {
    if (!streaming) {
      setShown(content);
      return;
    }
    setShown("");
    if (!content) return;
    let i = 0;
    const total = content.length;
    // Adaptive speed: longer texts type faster.
    const step = total > 1200 ? 12 : total > 400 ? 6 : 3;
    const tick = total > 1200 ? 8 : 14;
    const id = setInterval(() => {
      i = Math.min(total, i + step);
      setShown(content.slice(0, i));
      if (i >= total) clearInterval(id);
    }, tick);
    return () => clearInterval(id);
  }, [content, streaming]);

  const isUser = role === "user";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className={cn("flex gap-2 animate-fade-in", isUser ? "flex-row-reverse" : "")}>
      <div
        className={cn(
          "shrink-0 h-8 w-8 rounded-full flex items-center justify-center shadow-sm ring-1 ring-border/50",
          isUser
            ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground"
            : "bg-gradient-to-br from-muted to-muted/60",
        )}
      >
        {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4 text-primary" />}
      </div>
      <div
        className={cn(
          "group relative max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm transition-shadow",
          isUser
            ? "bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-tr-sm"
            : "bg-card border rounded-tl-sm hover:shadow-md",
        )}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap leading-relaxed" dir="auto">{content}</div>
        ) : (
          <>
            <div className="prose prose-sm dark:prose-invert max-w-none leading-relaxed prose-p:my-1 prose-p:leading-relaxed prose-ul:my-1 prose-ul:pl-5 prose-ol:my-1 prose-ol:pl-5 prose-li:my-0 prose-li:marker:text-primary/60 prose-headings:mt-2.5 prose-headings:mb-1 prose-headings:font-semibold prose-h1:text-base prose-h2:text-[15px] prose-h3:text-sm prose-pre:my-1.5 prose-pre:bg-muted prose-pre:text-foreground prose-pre:rounded-md prose-pre:p-2 prose-pre:text-xs prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0 prose-code:rounded prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:underline-offset-2 prose-a:break-words prose-strong:text-foreground prose-strong:font-semibold prose-table:my-1.5 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-th:border prose-td:border prose-th:border-border prose-td:border-border prose-th:bg-muted prose-blockquote:border-l-2 prose-blockquote:border-l-primary/40 prose-blockquote:pl-3 prose-blockquote:my-1.5 prose-blockquote:text-muted-foreground prose-blockquote:not-italic prose-hr:my-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0" dir="auto">
              {shown ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    table: ({ node, ...props }) => (
                      <div className="overflow-x-auto my-2 rounded-md border">
                        <table className="w-full text-xs border-collapse" {...props} />
                      </div>
                    ),
                    a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                  }}
                >
                  {shown}
                </ReactMarkdown>
              ) : null}
              {streaming && shown.length < content.length && (
                <span className="inline-block w-1.5 h-3.5 ml-0.5 align-middle bg-primary/70 animate-pulse rounded-sm" />
              )}
            </div>
            {children}
            {content && !streaming && (
              <Button
                onClick={copy}
                variant="ghost"
                size="icon"
                className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity bg-background border shadow-sm"
                aria-label="Copy"
              >
                {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div className="flex gap-2 animate-fade-in">
      <div className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/60 ring-1 ring-border/50 shadow-sm">
        <Bot className="h-4 w-4 text-primary" />
      </div>
      <div className="rounded-2xl rounded-tl-sm px-4 py-3 bg-card border shadow-sm">
        <div className="flex gap-1 items-center">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce" />
        </div>
      </div>
    </div>
  );
}
